import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Printer, Shuffle, DownloadSimple, FilePdf } from "@phosphor-icons/react";

/* Save a branded PIN-sheet PDF (rendered server-side) to the browser */
async function downloadPinPdf(pins, punchUrl, filename) {
  const res = await api.post("/payroll/punch/employees/pins/pdf", { pins, punch_url: punchUrl }, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 100);
}

/* Set / reset one employee's punch PIN */
export function PinDialog({ employee, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const random = () => setPin(String(Math.floor(1000 + Math.random() * 9000)));

  const save = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("PIN must be 4–6 digits");
    setBusy(true);
    try {
      await api.put(`/payroll/punch/employees/${employee.id}/pin`, { pin });
      toast.success(`PIN set for ${employee.name} — share it privately`);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Remove ${employee.name}'s PIN? They will no longer be able to punch from mobile.`)) return;
    try { await api.delete(`/payroll/punch/employees/${employee.id}/pin`); toast.success("PIN removed"); onSaved?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const saveAndDownloadPdf = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("Enter or generate a PIN to download");
    setBusy(true);
    try {
      await api.put(`/payroll/punch/employees/${employee.id}/pin`, { pin }); // save so the PDF PIN is the live one
      await downloadPinPdf(
        [{ emp_code: employee.emp_code, name: employee.name, pin }],
        `${window.location.origin}/punch`,
        `punch-pin-${employee.emp_code}.pdf`,
      );
      toast.success(`PIN saved & PDF downloaded for ${employee.name}`);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-[440px]" data-testid="pin-dialog">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-[15px] font-semibold tracking-tight">Punch PIN</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            {employee.name} <span className="text-slate-400">· {employee.emp_code}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">New 4-digit PIN</div>
          <div className="flex gap-2">
            <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="e.g. 4821" className="h-10 text-[16px] font-mono-num tracking-[0.35em] flex-1" data-testid="pin-input" />
            <Button variant="outline" className="h-10 px-3 text-[12px] shrink-0" onClick={random} data-testid="pin-random"><Shuffle size={14} className="mr-1.5" /> Random</Button>
          </div>
        </div>

        <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-[11.5px] leading-relaxed text-slate-500">
          The employee signs in at <code className="font-mono text-slate-700">{window.location.origin}/punch</code> with code <b className="text-slate-700">{employee.emp_code}</b> + this PIN.
          {employee.pin_set ? " A PIN is already set — saving replaces it." : " No PIN set yet."}
        </div>

        <DialogFooter className="mt-1 flex items-center justify-between gap-2 border-t pt-3 sm:justify-between">
          {employee.pin_set
            ? <Button variant="ghost" className="h-8 px-2 text-[12px] text-red-600 hover:text-red-700 hover:bg-red-50" onClick={clear} data-testid="pin-clear">Remove PIN</Button>
            : <span />}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} className="h-8 text-[12px] text-slate-500">Cancel</Button>
            <Button variant="outline" onClick={saveAndDownloadPdf} disabled={busy} className="h-8 text-[12px]" data-testid="pin-save-pdf"><FilePdf size={14} className="mr-1.5" /> Download PDF</Button>
            <Button onClick={save} disabled={busy} className="h-8 text-[12px] bg-[#0F172A] text-white hover:bg-slate-800" data-testid="pin-save">{busy ? "Saving…" : "Save PIN"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Bulk-generate PINs; plain PINs are shown ONCE */
export function PinsGeneratedDialog({ result, onClose }) {
  const punchUrl = `${window.location.origin}/punch`;
  const text = result.pins.map((p) => `${p.emp_code}  ${p.name}  PIN ${p.pin}`).join("\n");
  const copy = () => { navigator.clipboard?.writeText(`Celsius punch login: ${punchUrl}\n\n${text}`); toast.success("Copied — paste into WhatsApp / print and hand out privately"); };
  const downloadCsv = () => {
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const header = ["Employee Code", "Name", "PIN", "Punch URL"].join(",");
    const lines = result.pins.map((p) => [esc(p.emp_code), esc(p.name), esc(p.pin), esc(punchUrl)].join(","));
    const csv = "\uFEFF" + [header, ...lines].join("\r\n"); // BOM so Excel reads UTF-8
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `celsius-punch-pins-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 100);
    toast.success("PIN sheet downloaded (.csv)");
  };
  const downloadPdf = async () => {
    try {
      await downloadPinPdf(
        result.pins.map((p) => ({ emp_code: p.emp_code, name: p.name, pin: p.pin })),
        punchUrl,
        `celsius-punch-pins-${new Date().toISOString().slice(0, 10)}.pdf`,
      );
      toast.success("PIN sheet downloaded (.pdf)");
    } catch (e) {
      toast.error("Could not generate the PDF — try again");
    }
  };
  const print = () => {
    const w = window.open("", "_blank", "width=720,height=900");
    if (!w) return;
    w.document.write(`<html><head><title>Celsius · Punch PINs</title><style>body{font-family:IBM Plex Sans,Arial;padding:24px}h1{font-size:18px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ddd;padding:6px 10px;font-size:13px;text-align:left}th{background:#0F172A;color:#fff}code{font-family:IBM Plex Mono,monospace;font-size:15px;letter-spacing:.2em}</style></head><body><h1>Celsius · Employee Punch PINs</h1><p>Sign in at <b>${window.location.origin}/punch</b> with your Employee Code and PIN. Keep your PIN private.</p><table><tr><th>Code</th><th>Employee</th><th>PIN</th></tr>${result.pins.map((p) => `<tr><td>${p.emp_code}</td><td>${p.name}</td><td><code>${p.pin}</code></td></tr>`).join("")}</table></body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-[560px]" data-testid="pins-generated-dialog">
        <DialogHeader><DialogTitle className="text-sm">{result.count} PIN{result.count === 1 ? "" : "s"} generated — shown only once</DialogTitle></DialogHeader>
        <div className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">Copy or print this list now. PINs are stored hashed and cannot be viewed again — you can always set a new one per employee.</div>
        <div className="border border-slate-200 rounded-sm max-h-[50vh] overflow-y-auto text-[12px] divide-y divide-slate-100" data-testid="pins-list">
          {result.pins.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-3 py-1.5" data-testid={`pin-row-${p.emp_code}`}>
              <span className="font-mono-num text-slate-500 w-20">{p.emp_code}</span>
              <span className="flex-1 font-semibold text-slate-900">{p.name}</span>
              <code className="font-mono-num text-[15px] tracking-[0.25em] text-[#0F172A]">{p.pin}</code>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" className="h-8 text-[12px] text-slate-500" onClick={copy} data-testid="pins-copy"><Copy size={13} className="mr-1" /> Copy all</Button>
          <Button variant="outline" className="h-8 text-[12px]" onClick={print} data-testid="pins-print"><Printer size={13} className="mr-1" /> Print</Button>
          <Button variant="outline" className="h-8 text-[12px]" onClick={downloadCsv} data-testid="pins-download-csv"><DownloadSimple size={13} className="mr-1" /> .csv</Button>
          <Button className="h-8 text-[12px] bg-[#0F172A] text-white hover:bg-slate-800" onClick={downloadPdf} data-testid="pins-download-pdf"><FilePdf size={13} className="mr-1" /> Download PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
